import { PANEL_OPERATION_METADATA } from '@exylon/generated-contract-kernel/types';
import type { PanelLiveScreenId } from '../foundation/runtime-port';

export type PanelOperation = keyof typeof PANEL_OPERATION_METADATA;

/** Each screen's operations, in their order, as the screen's definition lists them. */
export type ScreenOperations = Readonly<Record<PanelLiveScreenId, readonly PanelOperation[]>>;

/**
 * The form of every screen's operations the entry carries: for each screen, the positions of its operations in the
 * generated operation registry, in the screen's order, each a fixed-width base-36 number. The names stay in the
 * registry and in the screens' ports; the entry loads no port to know what a screen may dispatch.
 */
export type ScreenAccessManifest = Readonly<Record<string, string>>;

/** The registry's operations in their generated order: the positions a manifest names. */
const vocabulary = Object.keys(PANEL_OPERATION_METADATA) as PanelOperation[];
const width = (() => {
  let digits = 1;
  while (36 ** digits < vocabulary.length) digits += 1;
  return digits;
})();

/** The manifest of these screens' operations (the build, and the specs, make it from the screens' definition). */
export function encodeScreenAccess(screens: ScreenOperations): ScreenAccessManifest {
  return Object.freeze(Object.fromEntries(Object.entries(screens).map(([screen, operations]) => [screen,
    operations.map((operation) => {
      const position = vocabulary.indexOf(operation);
      if (position < 0) throw new Error(`screen_access_unknown_operation:${screen}:${operation}`);
      return position.toString(36).padStart(width, '0');
    }).join('')])));
}

/** One screen's operations, in their order, read back from the manifest. */
export function screenOperations(manifest: ScreenAccessManifest, screen: PanelLiveScreenId): readonly PanelOperation[] {
  const positions = manifest[screen];
  if (positions === undefined || positions.length % width !== 0) throw new Error(`screen_access_manifest_invalid:${screen}`);
  return Object.freeze(Array.from({ length: positions.length / width }, (_, index) => {
    const operation = vocabulary[Number.parseInt(positions.slice(index * width, (index + 1) * width), 36)];
    if (operation === undefined) throw new Error(`screen_access_manifest_invalid:${screen}`);
    return operation;
  }));
}
